import { useSelectedPaymentInformation } from "@apac/checkout";
import {
  ApacHotelsComponent,
  RewardsAndCreditsBreakdownSection,
  useApacHotelsStyles,
} from "@apac/components/hotels";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { Product } from "@b2bportal/purchase-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon, Popover, PopoverContent, PopoverTrigger } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./styles.module.scss";
import { useHotelCheckoutTotals } from "./useHotelCheckoutTotals";
import { BonusEarnPill } from "@/modules/wallet";
import { useBalanceWithPoints } from "@/overrides/apac/apac-checkout";
import { getEarnRateByBin } from "@/utilities/avionEarnRates";

export interface TotalAmountLinesProps {
  className?: string;
  hasFulfilled?: boolean;
  fiatFormatOptions?: Intl.NumberFormatOptions;
}

const COMPONENT_KEY = ApacHotelsComponent.TotalAmountLines;

export const TotalAmountLines = ({
  className,
  hasFulfilled = false,
  fiatFormatOptions,
}: TotalAmountLinesProps) => {
  const styles = useApacHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t, formatFiatCurrency } = useI18nContext();

  const { tripTotalWithFintech, dueNowTotalWithFintech } =
    useHotelCheckoutTotals();

  const { remainingBalance, rewardsAndCreditsLineItems, isConfirmationPage } =
    useBalanceWithPoints(Product.Hotel, dueNowTotalWithFintech);
  const selectedPayment = useSelectedPaymentInformation();
  const earnsBonusPoints = getEarnRateByBin(selectedPayment?.bin) != null;

  return (
    <div className={clsx(block, className)}>
      <PriceLine
        value={formatFiatCurrency(
          tripTotalWithFintech?.fiat,
          fiatFormatOptions
        )}
        label={t("total")}
        type="total"
      />

      <RewardsAndCreditsBreakdownSection
        lineItems={rewardsAndCreditsLineItems}
      />

      {!hasFulfilled && (
        <>
          <PriceLine
            value={formatFiatCurrency(remainingBalance, fiatFormatOptions)}
            label={isConfirmationPage ? t("trips.amountPaid") : t("amountDue")}
            type="amount-due"
          />
          {earnsBonusPoints && (
            <BonusEarnPill
              variant={isConfirmationPage ? "earned" : "earning"}
              className={cn("bonus-earn-pill")}
            />
          )}
        </>
      )}
    </div>
  );
};

export const PriceLine = ({
  value,
  label,
  type,
  tooltip,
}: {
  value: string;
  label: string;
  type: string;
  tooltip?: string;
}) => {
  const styles = useApacHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);

  return (
    <div className={cn("item", [type])}>
      {tooltip && (
        <Popover>
          <PopoverTrigger>
            <div className={cn("item-label", [type])}>
              {label}
              <Icon className="tooltip-icon" iconName={IconNameEnum.info} />
            </div>
          </PopoverTrigger>
          <PopoverContent
            className={cn("tooltip-content")}
            showCloseButton={false}
          >
            {tooltip}
          </PopoverContent>
        </Popover>
      )}
      {!tooltip && <span className={cn("item-label", [type])}>{label}</span>}
      <span className={cn("item-value", [type])}>{value}</span>
    </div>
  );
};
